<template>
  <div>
    <v-dialog v-model="isOpen" max-width="720" persistent>
      <v-card>
        <v-card-title>
          <div class="d-flex align-center justify-space-between w-100">
            <h1>{{ cardTitle }}</h1>
            <v-btn icon @click="close()" class="align-self-end">
              <v-icon> mdi-close </v-icon>
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text v-if="isLoading" class="py-12 d-flex align-center justify-center">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </v-card-text>
        <v-card-text v-else class="py-4">
          <v-row>
            <v-col v-if="shouldShowCanNotEditMessage" class="mt-0 pt-0" cols="12">
              <div class="d-flex">
                <v-icon small class="mr-1"> mdi-information-outline </v-icon>
                <span>Não é possível editar um adicional que pertence a um usuário deletado!</span>
              </div>
            </v-col>
            <v-col cols="6" class="pb-0">
              <v-autocomplete
                :items="aditionalTypeItems"
                v-model="aditional.isDirectDeliverer"
                label="Tipo de usuário"
                :readonly="shouldDisableEdit"
                :disabled="shouldDisableEdit"
              />
            </v-col>
            <v-col cols="6" class="pb-0">
              <v-autocomplete
                :items="aditionalUsersAndDeletedUser"
                v-model="aditional.applicationUserId"
                item-text="name"
                item-value="aspNetUserId"
                :label="aditionalUserLabel"
                :readonly="shouldDisableEdit"
                :disabled="shouldDisableEdit"
              />
            </v-col>
            <v-col cols="6" class="pb-0">
              <v-autocomplete
                v-model="aditional.isDiscount"
                :items="isDiscountItems"
                item-value="value"
                label="Tipo de adicional"
                :readonly="shouldDisableEdit"
                :disabled="shouldDisableEdit"
                @input="onIsDiscountChange"
              />
            </v-col>
            <v-col cols="6" class="pb-0">
              <v-text-field
                v-if="isOpen"
                label="Valor"
                v-model="aditional.price"
                v-money="money"
                :readonly="shouldDisableEdit"
                :disabled="shouldDisableEdit"
              />
            </v-col>
            <v-col cols="6" class="pb-0">
              <v-menu
                ref="menu"
                v-model="isDatePickerVisible"
                :close-on-content-click="true"
                transition="scale-transition"
                offset-y
                min-width="auto"
                :disabled="shouldDisableEdit"
                :disabled-keys="shouldDisableEdit"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="formattedDate"
                    label="Data de criação"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    :disabled="shouldDisableEdit"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="aditional.additionalDate"
                  locale="pt-br"
                  :readonly="shouldDisableEdit"
                  :disabled="shouldDisableEdit"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="6" class="pb-0">
              <v-autocomplete
                v-model="aditional.justificationId"
                :items="justifications"
                item-text="name"
                item-value="id"
                label="Justificativa"
                :readonly="shouldDisableEdit"
                :disabled="shouldDisableEdit"
              />
            </v-col>
            <v-col cols="12" md="6" class="pb-0">
              <v-text-field
                :label="stopOrBoardingFieldLabel"
                v-model="stopOrBoardingId"
                :readonly="shouldDisableEdit"
                :disabled="shouldDisableEdit"
                :rules="[rules.isValidId]"
                v-mask="'NNNNNNNN-NNNN-NNNN-NNNN-NNNNNNNNNNNN'"
              />
            </v-col>
            <v-col cols="12" md="6" class="pb-0">
              <v-text-field
                label="Chave da NFE"
                v-model="aditional.nfeNumber"
                v-mask="'############################################'"
                type="text"
                :readonly="shouldDisableEdit"
                :disabled="shouldDisableEdit"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6" class="py-1">
              <v-autocomplete
                label="Remetente"
                v-model="aditional.senderId"
                :items="senders"
                item-text="name"
                item-value="id"
                :readonly="shouldDisableEdit"
                :disabled="shouldDisableEdit"
                clearable
              />
            </v-col>
            <v-col cols="12" md="6" class="d-flex flex-column justify-center py-1">
              <v-checkbox
                v-model="aditional.isLink"
                label="Avaria operacional"
                hide-details
                class="pb-2"
                :readonly="shouldDisableEdit"
                :disabled="shouldDisableEdit || !aditional.isDiscount"
              />
              <span v-if="!aditional.isDiscount" class="text-justify"> Somente débitos </span>
            </v-col>

            <v-col cols="12" class="pb-0">
              <v-textarea
                v-model="aditional.observation"
                label="Observação"
                :readonly="shouldDisableEdit"
                :disabled="shouldDisableEdit"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="d-flex pb-3 px-5">
          <v-btn v-if="!shouldDisableEdit" @click="close" class="flex-grow-1" dark color="red"> Cancelar </v-btn>
          <v-btn
            v-if="!shouldDisableEdit"
            @click="handleAditionalEdition"
            :loading="isEditButtonLoading"
            :disabled="shouldDisableEditButton"
            :dark="!shouldDisableEditButton"
            class="flex-grow-1"
            color="green"
          >
            Salvar
          </v-btn>
          <v-btn v-if="shouldDisableEdit" @click="close" class="flex-grow-1" color="info"> Voltar </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <MessagesError :errors="errors" :type="errorsType" :error500="error500" @closeMessages="errors = []" />
  </div>
</template>

<script>
import { VMoney } from 'v-money';
import { mapActions } from 'vuex';
import aditionalUsersType from '@/consts/AditionalUsersType';
import MessagesError from '@/components/MessagesError.vue';
export default {
  components: {
    MessagesError
  },
  props: {
    isOpen: {
      default: false,
      type: Boolean
    },
    aditionalToEdit: {
      default: () => ({
        id: null,
        applicationUserId: null,
        isDirectDeliverer: null,
        isDiscount: null,
        price: null,
        additionalDate: null,
        observation: null,
        boardingId: null,
        stopId: null,
        paymentOrderId: null,
        senderId: null,
        isLink: null,
        nfeNumber: null
      }),
      type: Object
    }
  },

  directives: { money: VMoney },

  data: () => ({
    deletedUserName: null,
    isLoading: false,
    aditional: {
      id: null,
      applicationUserId: null,
      isDirectDeliverer: true,
      isDiscount: null,
      price: null,
      additionalDate: null,
      observation: null,
      boardingId: null,
      stopId: null,
      justificationId: null
    },
    stopOrBoardingId: null,
    isDatePickerVisible: false,
    isEditButtonLoading: false,
    aditionalType: null,
    aditionalUsersType: aditionalUsersType,
    senders: [],
    justifications: [],
    transhipmentUsers: [],
    directDeliveresUsers: [],
    aditionalTypeItems: [
      { text: 'Entregador direto', value: true },
      { text: 'Transbordo', value: false }
    ],
    isDiscountItems: [
      { text: 'Crédito', value: false },
      { text: 'Débito', value: true }
    ],
    money: {
      decimal: ',',
      thousands: '.',
      prefix: 'R$ ',
      suffix: '',
      precision: 2,
      masked: false
    },
    errors: [],
    errorsType: 'error',
    error500: null,
    rules: {
      isValidId: (value) => {
        if (value === null || value === '') {
          return true;
        } else if (value.length === 36 && /^[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}$/.test(value)) {
          return true;
        } else {
          return 'ID inválido!';
        }
      }
    }
  }),

  computed: {
    cardTitle() {
      return `${this.hasPaymentOrder ? 'Visualizar' : 'Editar'} adicional`;
    },
    aditionalUsersAndDeletedUser() {
      if (this.deletedUserName) {
        return [...this.selectedAditionalUserTypeItems, { aspNetUserId: '0', name: this.deletedUserName }];
      }
      return this.selectedAditionalUserTypeItems;
    },
    hasDeletedUser() {
      return this.aditional.applicationUserId === '0';
    },
    selectedAditionalUserTypeItems() {
      return this.aditional.isDirectDeliverer ? this.directDeliveresUsers : this.transhipmentUsers;
    },
    stopOrBoardingFieldLabel() {
      return this.aditional.isDirectDeliverer ? 'ID do embarque' : 'ID da parada';
    },
    aditionalUserLabel() {
      return this.aditional.isDirectDeliverer ? 'Entregador direto' : 'Transbordo';
    },
    formattedDate() {
      const date = this.aditional.additionalDate;
      if (date === null) return '';
      const [year, month, day] = date.split('-');
      return `${day}/${month}/${year}`;
    },
    shouldDisableEditButton() {
      if (this.aditional.justificationId === null) return true;
      const { applicationUserId, price, justificationId } = this.aditional;
      const hasValidId =
        this.stopOrBoardingId !== null &&
        this.stopOrBoardingId.length === 36 &&
        /^[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}$/.test(this.stopOrBoardingId);
      return (
        !applicationUserId ||
        this.transformPriceToSend(price) === 0 ||
        !justificationId ||
        (this.stopOrBoardingId === null || this.stopOrBoardingId === '' ? false : !hasValidId)
      );
    },
    hasPaymentOrder() {
      return this.aditional.paymentOrderId !== '00000000-0000-0000-0000-000000000000';
    },
    shouldDisableEdit() {
      return this.hasPaymentOrder || this.hasDeletedUser;
    },
    shouldShowCanNotEditMessage() {
      return !this.hasPaymentOrder && this.hasDeletedUser;
    },
    hasJustification() {
      return this.aditional.justificationId != null;
    }
  },

  watch: {
    isOpen: {
      async handler(isOpen) {
        if (isOpen) {
          this.isLoading = true;
          this.stopOrBoardingId = null;
          const {
            boardingId,
            stopId,
            additionalDate: dateToFormat,
            id,
            isDiscount,
            price,
            observation,
            typeUser,
            name,
            justification,
            paymentOrderId,
            nameSender: sender,
            isLink,
            accessNfe: nfeNumber
          } = this.aditionalToEdit;

          const isDirectDeliverer = typeUser === 'Entregador Direto';
          this.aditional.isDirectDeliverer = isDirectDeliverer;

          const applicationUserId = this.getUserIdIfExistsByName(name);

          const additionalDate = dateToFormat.slice(0, 10);

          await this.getJustifications(isDiscount);

          const senderId = sender ? this.senders.find((item) => item.name === sender).id : null;

          if (boardingId) this.stopOrBoardingId = boardingId;
          else if (stopId) this.stopOrBoardingId = stopId;

          const justificationId = this.setJustification(justification);

          this.aditional = {
            boardingId,
            stopId,
            additionalDate,
            id,
            isDiscount,
            price: Number((price * 100).toFixed(2)),
            observation,
            applicationUserId,
            isDirectDeliverer,
            justificationId,
            paymentOrderId,
            senderId,
            isLink,
            nfeNumber
          };
          this.isLoading = false;
        }
      }
    },
    'aditional.isDiscount': {
      handler(isDiscount, oldIsDiscount) {
        if (oldIsDiscount !== null) {
          this.getJustifications(isDiscount);
        }
        this.handleIsLink(isDiscount);
      }
    }
  },

  async created() {
    this.transhipmentUsers = await this.getTranshipmentForPaymentOrder();
    this.directDeliveresUsers = await this.getDirectDeliverersForPaymentOrder();
    this.justifications = await this.fetchJustificationsById(9);
    await this.getAllSenders();
  },

  methods: {
    ...mapActions('NfeStore', ['fetchJustificationsById']),
    ...mapActions('AccountsStore', [
      'getDirectDeliverersForPaymentOrder',
      'getTranshipmentForPaymentOrder',
      'editAditional',
      'getSenders'
    ]),

    async handleAditionalEdition() {
      const { price, isDirectDeliverer } = this.aditional;
      const aditionalToEdit = {
        ...this.aditional,
        price: this.transformPriceToSend(price),
        boardingId: isDirectDeliverer ? this.stopOrBoardingId : null,
        stopId: !isDirectDeliverer ? this.stopOrBoardingId : null
      };
      try {
        this.isEditButtonLoading = true;
        const data = await this.editAditional(aditionalToEdit);
        if (data.success) {
          this.handleSuccessFeedback();
        } else {
          this.errors = data.errors;
          this.errorsType = 'error';
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isEditButtonLoading = false;
        this.close();
      }
    },

    async getAllSenders() {
      try {
        this.senders = await this.getSenders();
      } catch (error) {
        console.error(error);
      }
    },

    handleIsLink(isDiscount) {
      if (!isDiscount) {
        this.aditional.isLink = false;
      }
    },

    async getJustifications(isAditionalDiscount = false) {
      const justificationsNumber = isAditionalDiscount ? 11 : 10;
      try {
        this.justifications = await this.fetchJustificationsById(justificationsNumber);
      } catch (error) {
        console.error(error);
      }
    },

    handleSuccessFeedback() {
      this.errors = ['Adicional editado com sucesso!'];
      this.errorsType = 'success';
      this.$emit('shouldUpdateList');
      this.close();
    },

    close() {
      this.$emit('update:isOpen', false);
    },

    getUserIdIfExistsByName(name) {
      const user = this.selectedAditionalUserTypeItems.find((item) => item.name === name);

      if (user === undefined) {
        this.deletedUserName = name;
        return '0';
      } else {
        this.deletedUserName = null;
        return user.aspNetUserId;
      }
    },

    setJustification(justification) {
      return this.justifications.find((item) => item.name == justification).id;
    },

    onIsDiscountChange() {
      this.aditional.justificationId = null;
    },

    transformPriceToSend(element) {
      if (!element) return null;
      var money = element.toString().trim();
      money = money.replace('R$ ', '').replaceAll('.', '').replace(',', '.');
      return Number(money);
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep.v-input__slot {
  margin: 0;
  padding: 0;

  &::after,
  &::before {
    display: none !important;
  }
}
::v-deep.v-input {
  .v-text-field {
    &__details {
      padding-top: 4px;
      display: inline-block;
    }
  }
}

::v-deep .v-input__append-inner.v-input__append-inner .v-input__icon--clear {
  padding-top: 17px;
}
</style>
